import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import Wordmark from '../components/Wordmark'

interface PublicLeague {
  id: string
  name: string
  join_code: string
  commissioner: string
  members: number
  max_members: number | null
  spots_left: number
  start_week: number
  end_week: number
  join_open: boolean
}

/**
 * For the person with four friends instead of eleven.
 *
 * The format needs a dozen managers to be any good — the burn only bites when
 * enough of the board is being spent — and "go find eleven people" is where
 * most of them would quietly stop. So: a list of rooms that already exist,
 * with an honest count of who is in them.
 */
export default function Discover() {
  const [leagues, setLeagues] = useState<PublicLeague[]>([])
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState<string | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const nav = useNavigate()

  useEffect(() => {
    void (async () => {
      const { data, error } = await supabase.rpc('list_public_leagues', { p_limit: 40 })
      if (error) setErr(error.message)
      else setLeagues((data ?? []) as PublicLeague[])
      setLoading(false)
    })()
  }, [])

  const join = async (l: PublicLeague) => {
    setBusy(l.id)
    setErr(null)
    const { error } = await supabase.rpc('join_league', {
      p_code: l.join_code,
      p_team_name: 'My Team',
    })
    setBusy(null)
    if (error) return setErr(error.message)
    nav(`/app/l/${l.id}`)
  }

  return (
    <div className="page">
      <header className="topbar">
        <Link className="wordmark" to="/app"><Wordmark /></Link>
        <Link className="btn btn-ghost" to="/app">My leagues</Link>
      </header>

      <main className="container narrow">
        <h1>Open leagues</h1>
        <p className="lede-sm">
          Leagues anyone can join. They cap at 15 on purpose — above that, two
          people submitting the same six rooms stops being a funny coincidence
          and starts happening most weeks.
        </p>

        {loading && <p className="muted">Looking…</p>}
        {err && <p className="error">{err}</p>}

        {!loading && leagues.length === 0 && (
          <div className="empty-card">
            <h2>Nobody's opened one yet</h2>
            <p className="muted">
              Be first — create a league and tick <strong>Open to anyone</strong>.
              It'll show up here for people who need a few more managers.
            </p>
            <Link className="btn btn-primary" to="/app/new">Create a league</Link>
          </div>
        )}

        <ul className="league-list">
          {leagues.map((l) => (
            <li key={l.id} className={l.join_open ? '' : 'is-full'}>
              <div className="ll-main">
                <h3>{l.name}</h3>
                <p className="muted">
                  run by {l.commissioner} · weeks {l.start_week}–{l.end_week}
                </p>
              </div>

              <div className="ll-fill" title={`${l.members} of ${l.max_members ?? '∞'}`}>
                {Array.from({ length: l.max_members ?? 12 }, (_, i) => (
                  <span key={i} className={i < l.members ? 'll-pip is-on' : 'll-pip'} />
                ))}
                <small>
                  {l.join_open
                    ? `${l.spots_left} spot${l.spots_left === 1 ? '' : 's'} left`
                    : 'Full'}
                </small>
              </div>

              <button
                className="btn btn-primary"
                disabled={!l.join_open || busy === l.id}
                onClick={() => join(l)}
              >
                {busy === l.id ? 'Joining…' : l.join_open ? 'Join' : 'Full'}
              </button>
            </li>
          ))}
        </ul>
      </main>
    </div>
  )
}
